/**
 * PrintingDocsHub — "الطباعة وتخليص المستندات" sub-services hub.
 * Mirrors the customer-platform service types, including طلبات التأشيرة والهوية.
 * Route: /control/cloudgm/service-requests/printing-docs
 */
import { useNavigate } from 'react-router-dom';
import { IdCard, Plane, FileCheck, Car, FileText } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';

const SUB_SERVICES = [
  {
    id: 'visa-id',
    icon: IdCard,
    titleAr: 'طلبات التأشيرة والهوية',
    titleEn: 'Visa & Emirates ID',
    descAr: 'التقديم على التأشيرة والهوية الإماراتية (بوت جيميناي)',
    descEn: 'Apply for UAE visa & Emirates ID (Gemini bot)',
    iconBg: 'bg-cyan-500/20',
    iconColor: 'text-cyan-500',
    borderHover: 'hover:border-cyan-500/50',
  },
  {
    id: 'tourist-visa',
    icon: Plane,
    titleAr: 'التأشيرات السياحية لجميع الدول',
    titleEn: 'Tourist Visas — All Countries',
    descAr: 'طلبات التأشيرات السياحية',
    descEn: 'Tourist visa requests',
    iconBg: 'bg-sky-500/20',
    iconColor: 'text-sky-500',
    borderHover: 'hover:border-sky-500/50',
  },
  {
    id: 'doc-certification',
    icon: FileCheck,
    titleAr: 'تصديق المستندات',
    titleEn: 'Document Certification',
    descAr: 'طلبات تصديق المستندات الرسمية',
    descEn: 'Official document certification requests',
    iconBg: 'bg-amber-500/20',
    iconColor: 'text-amber-500',
    borderHover: 'hover:border-amber-500/50',
  },
  {
    id: 'driving-license',
    icon: Car,
    titleAr: 'خدمات رخصة القيادة',
    titleEn: 'Driving License Services',
    descAr: 'طلبات خدمات رخصة القيادة',
    descEn: 'Driving license service requests',
    iconBg: 'bg-emerald-500/20',
    iconColor: 'text-emerald-500',
    borderHover: 'hover:border-emerald-500/50',
  },
  {
    id: 'printing-docs-general',
    icon: FileText,
    titleAr: 'الطلبات العامة',
    titleEn: 'General Requests',
    descAr: 'طلبات الطباعة وتخليص المستندات العامة',
    descEn: 'General printing & document clearing requests',
    iconBg: 'bg-zinc-500/20',
    iconColor: 'text-zinc-400',
    borderHover: 'hover:border-zinc-500/50',
  },
];

export default function PrintingDocsHub() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  return (
    <DashboardLayout
      title="الطباعة وتخليص المستندات"
      titleEn="Printing & Document Clearing"
      subtitle="اختر نوع الخدمة لإدارة طلباتها"
      subtitleEn="Pick a service type to manage its requests"
      screenCode="SRV-010"
      backPath="/manage/cloudgm?tab=services-categories"
      onBack={() => navigate('/manage/cloudgm?tab=services-categories')}
      breadcrumb={[
        { labelAr: 'CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC', path: '/manage/cloudgm' },
        { labelAr: 'منصة العملاء (الإشراف)', labelEn: 'Customer Platform (Supervision)', path: '/manage/cloudgm' },
        { labelAr: 'طلبات الخدمات', labelEn: 'Service Requests', path: '/manage/cloudgm?tab=services-categories' },
        { labelAr: 'الطباعة وتخليص المستندات', labelEn: 'Printing & Documents', path: null },
      ]}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="printing-docs-hub">
          {SUB_SERVICES.map(({ id, icon: Icon, titleAr, titleEn, descAr, descEn, iconBg, iconColor, borderHover }) => (
            <div
              key={id}
              onClick={() => navigate(`/control/cloudgm/service-requests/${id}`)}
              className={`group bg-white/[0.02] backdrop-blur-xl rounded-2xl p-8 border border-white/10 ${borderHover} transition-all cursor-pointer hover:-translate-y-1 hover:bg-white/[0.04]`}
              data-testid={`printing-sub-${id}`}
            >
              <div className="text-center">
                <div className={`w-16 h-16 ${iconBg} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon className={`h-8 w-8 ${iconColor}`} />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{isAr ? titleAr : titleEn}</h3>
                <p className="text-sm text-[#808080]">{isAr ? descAr : descEn}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </DashboardLayout>
  );
}
